import { ActionIcon, Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
    IconCheck,
    IconClock,
    IconExclamationCircle,
    IconPlus,
    IconTrash,
    IconX,
} from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Endpoints } from '@xver/apis';
import type { RetentionPolicyResponse } from '@xver/apis/v1/sammeln/retention/model';
import Button from '@xver/components/Atoms/Button/Button';
import ConfirmActionModal from '@xver/components/Atoms/ConfirmActionModal/ConfirmActionModal';
import DialogBox from '@xver/components/Atoms/DialogBox/DialogBox';
import { DataTable, DataTableColumn } from '@xver/components/Organisms';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    RETENTION_POLICIES_QUERY_KEY,
    useRetentionPoliciesQuery,
} from '../hooks/useRetentionPoliciesQuery';
import AddRetentionPolicyModal from './AddRetentionPolicyModal';

interface RetentionTabPanelProps {
    projectIdOrName: string;
}

export default function RetentionTabPanel({ projectIdOrName }: RetentionTabPanelProps) {
    const { t } = useTranslation('artifactRegistry');
    const queryClient = useQueryClient();
    const [addOpened, { open: openAdd, close: closeAdd }] = useDisclosure(false);
    const [deleteOpened, { open: openDelete, close: closeDelete }] = useDisclosure(false);
    const [pendingDelete, setPendingDelete] = useState<RetentionPolicyResponse | null>(null);

    const { data, isPending } = useRetentionPoliciesQuery(projectIdOrName);
    const policies = data?.policies ?? [];

    const deleteMutation = useMutation({
        mutationFn: (policy: RetentionPolicyResponse) =>
            Endpoints.v1.Sammeln.Retention.deleteRetentionPolicy(projectIdOrName, policy.id),
        onSuccess: async () => {
            notifications.show({
                title: t('retention.toast.deleteTitle', 'Policy deleted'),
                message: t('retention.toast.deleteMessage', 'The retention policy was removed.'),
                icon: <IconCheck size={18} color="#22c55e" />,
            });
            await queryClient.invalidateQueries({
                queryKey: [RETENTION_POLICIES_QUERY_KEY, projectIdOrName],
            });
        },
        onError: (err: { message?: string }) => {
            notifications.show({
                title: t('retention.toast.errorTitle', 'Action failed'),
                message:
                    err?.message ?? t('retention.toast.errorMessage', 'Please try again later.'),
                icon: <IconExclamationCircle size={18} color="#eb5757" />,
            });
        },
    });

    const handleDelete = (policy: RetentionPolicyResponse) => {
        setPendingDelete(policy);
        openDelete();
    };

    const confirmDelete = () => {
        if (!pendingDelete) return;
        deleteMutation.mutate(pendingDelete, {
            onSuccess: () => {
                closeDelete();
                setPendingDelete(null);
            },
        });
    };

    const columns: DataTableColumn<RetentionPolicyResponse>[] = [
        {
            key: 'type',
            label: t('retention.columns.type', 'Type'),
            render: (p) => <span className="text-sm font-medium text-fg capitalize">{p.type}</span>,
        },
        {
            key: 'scope',
            label: t('retention.columns.scope', 'Repository scope'),
            render: (p) => (
                <span className="text-sm text-fg/78">
                    {p.repository_name?.trim()
                        ? p.repository_name
                        : t('retention.allRepos', 'All repositories')}
                </span>
            ),
        },
        {
            key: 'max_count',
            label: t('retention.columns.maxCount', 'Max count'),
            render: (p) => <span className="text-xs text-fg/78">{p.max_count ?? '-'}</span>,
        },
        {
            key: 'max_age',
            label: t('retention.columns.maxAge', 'Max age (days)'),
            render: (p) => <span className="text-xs text-fg/78">{p.max_age_days ?? '-'}</span>,
        },
        {
            key: 'enabled',
            label: t('retention.columns.enabled', 'Enabled'),
            render: (p) =>
                p.enabled ? (
                    <span className="inline-flex items-center gap-1 text-xs text-green-400 light:text-green-800">
                        <IconCheck size={14} />
                        {t('retention.enabledTrue', 'Enabled')}
                    </span>
                ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-fg/68">
                        <IconX size={14} />
                        {t('retention.enabledFalse', 'Disabled')}
                    </span>
                ),
        },
    ];

    return (
        <>
            <DialogBox
                label={t('retention.title', 'Retention policies')}
                description={t(
                    'retention.description',
                    'Automatically clean up old or excess artifacts.'
                )}
                rightDescription={
                    <Button
                        size="sm"
                        variant="secondary"
                        leftIcon={<IconPlus size={16} />}
                        onClick={openAdd}
                    >
                        {t('retention.add', 'Add policy')}
                    </Button>
                }
            >
                <DataTable
                    caption={t('retention.title', 'Retention policies')}
                    columns={columns}
                    rows={policies}
                    isLoading={isPending}
                    rowKey={(p) => p.id}
                    emptyMessage={
                        <div className="w-full flex flex-col items-center justify-center gap-2 py-6">
                            <IconClock size={28} stroke={1.25} className="text-fg/40" />
                            <span className="text-sm text-fg/68">
                                {t(
                                    'retention.empty',
                                    'No retention policies. Add a policy to control image storage.'
                                )}
                            </span>
                        </div>
                    }
                    actions={(p) => (
                        <div className="flex flex-row justify-end items-center gap-1">
                            <Tooltip
                                label={t('retention.actions.delete', 'Delete policy')}
                                withArrow
                            >
                                <ActionIcon
                                    variant="subtle"
                                    color="red"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDelete(p);
                                    }}
                                    disabled={deleteMutation.isPending}
                                    aria-label={t('retention.actions.deleteAria', 'Delete policy')}
                                >
                                    <IconTrash size={16} stroke={1.5} />
                                </ActionIcon>
                            </Tooltip>
                        </div>
                    )}
                />
            </DialogBox>

            <AddRetentionPolicyModal
                opened={addOpened}
                onClose={closeAdd}
                projectIdOrName={projectIdOrName}
            />

            <ConfirmActionModal
                opened={deleteOpened}
                onClose={() => {
                    if (deleteMutation.isPending) return;
                    closeDelete();
                    setPendingDelete(null);
                }}
                title={t('retention.confirm.deleteTitle', 'Delete retention policy?')}
                message={t(
                    'retention.confirm.deleteMessage',
                    'This policy will be permanently removed.'
                )}
                confirmLabel={t('retention.confirm.deleteLabel', 'Delete policy')}
                confirmVariant="danger"
                isLoading={deleteMutation.isPending}
                onConfirm={confirmDelete}
            />
        </>
    );
}
